import history from "history/browser";
import {useAppDispatch, useAppSelector} from "../redux/stateHooks";
import {updateAppContext} from "../redux/slices/app";
import {useClearProduct} from "./useClearProduct";
import {RedirectType} from "../definitions/enums";
import {isDevelopmentBuild} from "../utils/constants";

/**
 * Provides the current app context and a function to change the current product
 * @remarks This is used to provide the current product context to all components
 * @public
 */
export const useAppContext = () => {
    const dispatch = useAppDispatch();
    const {clearProduct} = useClearProduct();
    const state = useAppSelector((state) => state.app.value);

    /**
     * Changes the current product
     * @remarks As we're transitioning products' one by one,
     * we need to perform a full web request to the server to ensure the correct product page is rendered.
     * The only exception being the productOptions component, which will be performed client side.
     * @param productId - The productId of the product
     * @param primaryId - The primaryId of the product, if applicable
     * @param path - The path to redirect to
     * @param type - The type of redirect to perform, defaults to RedirectType.Server
     * @public
     */
    const changeProduct = (productId: number, primaryId: number | undefined = undefined, path: string, type: RedirectType = RedirectType.Server) => {
        if (isDevelopmentBuild && type === RedirectType.Server) {
            console.warn("Redirecting to server in development mode is not supported, performing client side redirect instead");
            type = RedirectType.Client;
        }

        switch (type) {
            case RedirectType.Server:
                return performServerSideRedirect(path);
            case RedirectType.Client:
                return performClientSideRedirect(productId, primaryId, path);
            default:
                throw new Error(`Invalid redirect type: ${type}`);
        }
    };

    /**
     * Performs a server side redirect
     * @remarks This is used when we're transitioning products' one by one,
     * as we need to ensure the correct product page is rendered
     * @param path - The path to redirect to
     * @private
     */
    const performServerSideRedirect = (path: string) => window.location.href = path;

    /**
     * Redirects the user to the new product page all within the client
     * @remarks Currently only used within the productOptions component when changing products,
     * the marketers are required to transition entire product matrices at once
     * @param productId - The productId of the product
     * @param primaryId - The primaryId of the product, if applicable
     * @param path - The path to redirect to
     * @see productOptions.tsx
     * @private
     */
    const performClientSideRedirect = (productId: number, primaryId: number | undefined, path: string) => {
        // Update state with new values
        dispatch(updateAppContext({productId, primaryId}));

        // Clear artwork when changing products
        clearProduct();

        // Update URL with new productId and primaryId values
        history.push(path, {productId, primaryId});
    };

    return {...state, changeProduct}
}